// trash icon from hereo icons
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { TrashIcon } from '@heroicons/react/24/outline'
import { FC, Fragment, useEffect } from 'react'
import {
  Control,
  FieldError,
  FieldErrors,
  useFieldArray,
  useForm,
  UseFormRegister,
  UseFormReturn,
} from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { DynamicFormProps } from '@ui/interfaces'
import { isValidUrl } from '@ui/lib/helpers/validationHelpers'
// TODO: GET rid of classNames dependency use clsx instead
import classNames from '@ui/lib/utils/classNames'
import { SaveAndCancelButtons } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SaveAndCancelButtons'
import * as Yup from 'yup'

enum Exporter {
  GRPC = 'grpc',
  PROTO = 'proto',
}

enum DataType {
  Log = 'log',
  Metric = 'metric',
  Trace = 'span',
}

enum FormType {
  Simple = 'Simple',
  Advanced = 'Advanced',
}

type Header = { value: string; name: string }

type GenericForm = {
  exporter: Exporter
  headers?: Header[]
  formType: FormType
  ['simple-host']?: string
  ['simple-port']?: string
  ['advanced-log-host']?: string
  ['advanced-log-path']?: string
  ['advanced-log-port']?: string
  ['advanced-metric-host']?: string
  ['advanced-metric-path']?: string
  ['advanced-metric-port']?: string
  ['advanced-span-host']?: string
  ['advanced-span-path']?: string
  ['advanced-span-port']?: string
}

const EXPORTER_LABEL_MAP: Record<Exporter, string> = {
  [Exporter.GRPC]: 'gRPC',
  [Exporter.PROTO]: 'Protobuf',
}

const EXPORTER_TO_PORT_MAP: Record<Exporter, number> = {
  [Exporter.GRPC]: 4317,
  [Exporter.PROTO]: 4318,
}

const DATATYPE_LABEL_MAP: Record<DataType, string> = {
  [DataType.Log]: 'Logs URL*',
  [DataType.Metric]: 'Metrics URL*',
  [DataType.Trace]: 'Traces URL*',
}

const DATATYPE_TO_PATH_MAP: Record<DataType, string> = {
  [DataType.Log]: '/v1/logs',
  [DataType.Metric]: '/v1/metrics',
  [DataType.Trace]: '/v1/traces',
}

type ListBoxOptionProps = {
  value: string
  label: string
}

const ListBoxOption: FC<ListBoxOptionProps> = ({ value, label }) => {
  return (
    <Listbox.Option
      className={({ active }) =>
        classNames(
          active ? 'bg-primary-500/90 text-white' : 'text-gray-900',
          'relative cursor-default select-none py-2 pl-3 pr-9'
        )
      }
      value={value}>
      {({ selected, active }) => (
        <>
          <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>{label}</span>

          {selected ? (
            <span
              className={classNames(
                active ? 'text-white' : 'text-primary-500',
                'absolute inset-y-0 right-0 flex items-center pr-4'
              )}>
              <CheckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      )}
    </Listbox.Option>
  )
}

type ExporterSelectProps = {
  onChange: (exporter: Exporter) => void
  value: Exporter
}

const ExporterSelect: FC<ExporterSelectProps> = ({ onChange, value }) => {
  return (
    <div className="w-48">
      <legend className="text-sm font-semibold leading-6 text-gray-900">Protocol*</legend>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <div className="relative h-full">
              <Listbox.Button
                data-testid="search-pipeline-selector"
                className="relative h-full w-48 min-w-full cursor-pointer truncate border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm">
                <span className="block truncate">{EXPORTER_LABEL_MAP[value]}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <Listbox.Options className="absolute z-10 max-h-60 w-full overflow-auto rounded-b-md bg-white text-base ring-1 ring-black/5 sm:text-sm">
                  {Object.values(Exporter).map((exporter) => (
                    <ListBoxOption key={exporter} label={EXPORTER_LABEL_MAP[exporter]} value={exporter} />
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}

type HeadersProps = {
  control: Control<GenericForm>
  register: UseFormRegister<GenericForm>
  errors: FieldErrors<GenericForm>
}

const Headers: FC<HeadersProps> = ({ control, errors, register }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
  })

  return (
    <>
      <div className="mt-12">
        <fieldset>
          <div className="flex items-center gap-x-4">
            <legend className="text-sm font-semibold leading-6 text-gray-900">Headers</legend>
            <button
              type="button"
              className="self-center rounded-md bg-primary-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => append({ name: '', value: '' })}>
              + Add
            </button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="mt-4">
              <div className="grid grid-cols-11 gap-4 text-sm">
                <input
                  {...register(`headers.${index}.name` as const)}
                  className="col-span-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Header Name"
                />
                <input
                  {...register(`headers.${index}.value` as const)}
                  className="col-span-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Header Value"
                />
                <button
                  type="button"
                  className="justify-self-center rounded-md bg-red-600 p-3 text-xs font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => remove(index)}>
                  <TrashIcon className="h-4 w-4" />
                </button>
                <div className="col-span-5">
                  {errors?.headers?.[index]?.name && (
                    <p className="text-xs italic text-red-500">{errors?.headers?.[index]?.name?.message}</p>
                  )}
                </div>
                <div className="col-span-5">
                  {errors?.headers?.[index]?.value && (
                    <p className="text-xs italic text-red-500">{errors?.headers?.[index]?.value?.message}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </fieldset>
      </div>
    </>
  )
}

type ErrMessageProps = {
  fieldError?: FieldError
}

const ErrMessage: FC<ErrMessageProps> = ({ fieldError }) => {
  if (!fieldError?.message) return null

  return (
    <div className="sm:col-span-12">
      <p className="text-xs italic text-red-500">{fieldError.message}</p>
    </div>
  )
}

type FormProps = {
  form: UseFormReturn<GenericForm>
  disabledInputs?: boolean
}

const SimpleForm: FC<FormProps> = ({ form, disabledInputs }) => {
  const {
    register,
    watch,
    formState: { errors },
    setValue,
  } = form

  const exporter = watch('exporter')

  const host = 'simple-host'
  const port = 'simple-port'

  return (
    <div>
      <ExporterSelect
        onChange={(exporter) => {
          if (disabledInputs) return
          setValue('exporter', exporter, { shouldDirty: true })
        }}
        value={exporter}
      />
      <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-12">
        <div className="sm:col-span-8">
          <label htmlFor={host} className="block text-sm font-medium leading-6 text-gray-900">
            Host
          </label>
          <div className="mt-2">
            <input
              type="text"
              id={host}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="https://example.com"
              {...register(host)}
              disabled={disabledInputs}
            />
          </div>
          <ErrMessage fieldError={errors[host]} />
        </div>
        <div className="sm:col-span-4">
          <label htmlFor={port} className="block text-sm font-medium leading-6 text-gray-900">
            Port
          </label>
          <div className="mt-2">
            <input
              type="text"
              id={port}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder={`${EXPORTER_TO_PORT_MAP[exporter]}`}
              {...register(port)}
              disabled={disabledInputs}
            />
          </div>
          <ErrMessage fieldError={errors[port]} />
        </div>
      </div>
    </div>
  )
}

const AdvancedForm: FC<FormProps> = ({ form, disabledInputs }) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = form

  const exporter = watch('exporter')

  return (
    <div>
      <ExporterSelect
        onChange={(exporter) => {
          if (disabledInputs) return
          setValue('exporter', exporter, { shouldDirty: true })
        }}
        value={exporter}
      />
      <div className="mt-6 flex flex-col gap-4">
        {Object.values(DataType).map((dataType) => {
          const host: keyof GenericForm = `advanced-${dataType}-host`
          const path: keyof GenericForm = `advanced-${dataType}-path`
          const port: keyof GenericForm = `advanced-${dataType}-port`

          return (
            <Fragment key={dataType}>
              <div className="grid gap-x-6 gap-y-8 sm:grid-cols-12">
                <div className="flex items-end text-sm font-medium leading-6 text-gray-900 sm:col-span-2">
                  {DATATYPE_LABEL_MAP[dataType]}
                </div>
                <div className={exporter === Exporter.GRPC ? 'sm:col-span-7' : 'sm:col-span-4'}>
                  <label htmlFor={host} className="block text-sm font-medium leading-6 text-gray-900">
                    Host
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id={host}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="https://example.com"
                      {...register(host)}
                      disabled={disabledInputs}
                    />
                  </div>
                  <ErrMessage fieldError={errors[host]} />
                </div>
                {exporter !== Exporter.GRPC && (
                  <div className="sm:col-span-3">
                    <label htmlFor={path} className="block text-sm font-medium leading-6 text-gray-900">
                      Path
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        id={path}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder={DATATYPE_TO_PATH_MAP[dataType]}
                        {...register(path)}
                        disabled={disabledInputs}
                      />
                    </div>
                    <ErrMessage fieldError={errors[path]} />
                  </div>
                )}
                <div className="sm:col-span-3">
                  <label htmlFor={`${dataType}-port`} className="block text-sm font-medium leading-6 text-gray-900">
                    Port
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id={`${dataType}-port`}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder={`${EXPORTER_TO_PORT_MAP[exporter]}`}
                      {...register(port)}
                      disabled={disabledInputs}
                    />
                  </div>
                  <ErrMessage fieldError={errors[port]} />
                </div>
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

type TabsProps<T extends string> = {
  tabs: T[]
  selectedTab: T
  onChange: (tab: T) => void
}

const Tabs = <T extends string>(props: TabsProps<T>) => {
  const { tabs, selectedTab, onChange } = props

  return (
    <div className="border-b border-gray-200 pb-5 sm:pb-0">
      <nav className="-mb-px flex space-x-8">
        {tabs.map((tab) => (
          <div
            key={tab}
            className={classNames(
              selectedTab === tab
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              'cursor-pointer whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium'
            )}
            onClick={() => onChange(tab)}>
            {tab}
          </div>
        ))}
      </nav>
    </div>
  )
}

const URLSchema = (schema: Yup.StringSchema) => schema.test('is-url-valid', 'URL is not valid', isValidUrl).required()

const simpleFormSchemaParts = {
  ['simple-host']: Yup.string().when('formType', {
    is: FormType.Simple,
    then: URLSchema,
  }),
  ['simple-port']: Yup.string(),
}

const advancedFormSchemaParts = {
  ['advanced-log-host']: Yup.string().when('formType', {
    is: FormType.Advanced,
    then: URLSchema,
  }),
  ['advanced-log-path']: Yup.string(),
  ['advanced-log-port']: Yup.string(),
  ['advanced-metric-host']: Yup.string().when('formType', {
    is: FormType.Advanced,
    then: URLSchema,
  }),
  ['advanced-metric-path']: Yup.string(),
  ['advanced-metric-port']: Yup.string(),
  ['advanced-span-host']: Yup.string().when('formType', {
    is: FormType.Advanced,
    then: URLSchema,
  }),
  ['advanced-span-path']: Yup.string(),
  ['advanced-span-port']: Yup.string(),
}

const genericFormSchema = Yup.object({
  exporter: Yup.string().oneOf(Object.values(Exporter)).required(),
  headers: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Header name is required'),
      value: Yup.string().required('Header value is required'),
    })
  ),
  ...simpleFormSchemaParts,
  ...advancedFormSchemaParts,
  formType: Yup.string().oneOf(Object.values(FormType)).required(),
}).required()

export default function GenericOtel(props: DynamicFormProps) {
  const form = useForm<GenericForm>({
    resolver: yupResolver(genericFormSchema),
    defaultValues: {
      exporter: Exporter.GRPC,
      formType: FormType.Simple,
      'advanced-log-path': DATATYPE_TO_PATH_MAP[DataType.Log],
      'advanced-metric-path': DATATYPE_TO_PATH_MAP[DataType.Metric],
      'advanced-span-path': DATATYPE_TO_PATH_MAP[DataType.Trace],
      ...props.config,
    },
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
    clearErrors,
  } = form

  const { saveClick, cancelClick } = props

  const formType = watch('formType')
  const exporter = watch('exporter')

  const onFormTypeChange = (formType: FormType) => {
    setValue('formType', formType)
    clearErrors()
  }

  useEffect(() => {
    const autofillPort = () => {
      if (!isDirty) return // only autofill in response to user changes to the form; don't overwrite the loaded values from the form

      const port = `${EXPORTER_TO_PORT_MAP[exporter]}`

      setValue('simple-port', port)
      setValue('advanced-log-port', port)
      setValue('advanced-metric-port', port)
      setValue('advanced-span-port', port)
    }

    autofillPort()
  }, [exporter, isDirty])

  return (
    <form onSubmit={handleSubmit(saveClick)}>
      {props.additionalInputs}
      <Tabs tabs={Object.values(FormType)} selectedTab={formType} onChange={onFormTypeChange} />
      <div className="pt-10">
        {formType === FormType.Simple ? (
          <SimpleForm form={form} disabledInputs={props.disableInputs} />
        ) : (
          <AdvancedForm form={form} disabledInputs={props.disableInputs} />
        )}
      </div>
      <Headers control={control} register={register} errors={errors} />
      <div className="mt-12" />
      {props.submitButton ? props.submitButton : <SaveAndCancelButtons cancelClick={cancelClick} />}
    </form>
  )
}
